// --- Core ---
import {first, sortBy} from "underscore";
// --- SaniQ ---
import {deserializeItem} from "./index";
import {
  deserializeAssignments,
  DeserializedRecord,
  deserializeManyRelationship,
  Deserializer,
  JSONAPIManyRelationship,
  JSONAPIRecord
} from "./Helpers";
import {Cron} from "helpers/forms/Cron";
import {QuestionType, SurveyCategory, SurveyRatedType} from "vendor/qurasoft/definitions/types/surveys";
import {Tag} from "./Tag";
import {deserializeSurveyTag, SurveyTag} from "./survey/Tag";
import {Goal, Task} from "./Task";
import {MeasurementKey} from "vendor/qurasoft/definitions/types/measurements";

interface Validation extends DeserializedRecord {
  subtype: string;
  validationType: "regex" | "required";
  value: string;
}

type ScheduleType = "cron" | "delay" | "once" | "permanent";

interface BaseQuestion extends DeserializedRecord {
  arrangement: number[];
  hint?: string;
  //is used when answering the survey in a session for optional questions
  optional_checked?: boolean;
  order: number;
  page: number;
  position: number;
  questionType: QuestionType;
  ratedType: null;
  //is used when answering the survey in a session
  response?: unknown;
  text: string;
  validations: Validation[];
}

interface EmptyQuestion extends BaseQuestion {
  questionType: "text";
}

interface EndSection extends BaseQuestion {
  questionType: "end_section";
}

interface Section extends Omit<BaseQuestion, "ratedType"> {
  questionType: "section";
  ratedType: SurveyRatedType | null;
}

interface DateQuestion extends BaseQuestion {
  questionType: "date";
  response?: Date;
}

interface MeasurementAnswer extends DeserializedRecord {
  valueTypes: MeasurementKey[];
}

type MeasurementResponse = Record<string, string[]>;

interface MeasurementQuestion extends BaseQuestion {
  questionType: "measurement";
  response?: MeasurementResponse;
  answer: MeasurementAnswer;
}

interface SimpleComponent extends DeserializedRecord<"simple_component"> {
  simpleId: string;
  componentId: string;
  name: string;
}

interface SimpleMedium extends DeserializedRecord<"simple_medium"> {
  simpleId: string;
  mediumId: string;
  name: string;
  notes?: string;
}

interface SimpleSurvey extends DeserializedRecord<"simple_survey"> {
  simpleId: string;
  surveyId: string;
  cron: Cron[];
  description?: string;
  hasHistory: boolean;
  name: string;
  scheduleType: ScheduleType;
}

interface SimpleAnswer extends DeserializedRecord {
  position: number;
  text: string;
  points?: number;
  components: Array<SimpleComponent>;
  media: Array<SimpleMedium>;
  surveys: Array<SimpleSurvey>;
  tasks: Array<Goal | Task>;
}

interface MultipleQuestion extends BaseQuestion {
  questionType: "multiple";
  answers: SimpleAnswer[];
  response?: {checked: boolean}[];
}

interface SingleQuestion extends BaseQuestion {
  questionType: "single";
  answers: SimpleAnswer[];
  response?: string;
}

interface SliderAnswer extends DeserializedRecord {
  startValue: number;
  endValue: number;
  steps: number;
  textInput: boolean;
}

interface SliderQuestion extends BaseQuestion {
  questionType: "slider";
  answer: SliderAnswer;
}

type Question = EmptyQuestion | EndSection | Section | DateQuestion | MeasurementQuestion | MultipleQuestion | SingleQuestion | SliderQuestion;

const isSection = (question: Question): question is Section => question.questionType === "section";

const isSimpleQuestion =
  (question: Question): question is SingleQuestion | MultipleQuestion => question.questionType === "single" || question.questionType === "multiple";

interface Schedule extends DeserializedRecord {
  cron?: Cron[];
  openDays?: number;
  openSymmetric?: boolean;
  scheduleType: ScheduleType;
}

interface Survey extends DeserializedRecord<"survey"> {
  category: SurveyCategory;
  copyright?: string;
  deletedAt?: Date;
  deleteDisabled: boolean;
  description: string;
  hasHistory: boolean;
  isAutomatic: boolean;
  key?: string;
  locked: boolean;
  name: string;
  questions: Question[];
  rated: boolean;
  ratedType: SurveyRatedType;
  schedules: Schedule[];
  version: number;
  tags: Tag[];
  surveyTagsAttributes: SurveyTag[]
}

const deserializeSimpleComponent = deserializeAssignments<SimpleComponent>(['simple', 'component']);
const deserializeSimpleMedium = deserializeAssignments<SimpleMedium>(['simple', 'medium']);
const deserializeSimpleSurvey = deserializeAssignments<SimpleSurvey>(['simple', 'survey']);

interface SimpleAnswerData extends JSONAPIRecord {
  relationships: {
    components: JSONAPIManyRelationship;
    simple_components: JSONAPIManyRelationship;
    simple_media: JSONAPIManyRelationship;
    simple_surveys: JSONAPIManyRelationship;
    tasks: JSONAPIManyRelationship;
  } & JSONAPIRecord["relationships"];
}

const deserializeSimpleAnswer: Deserializer<SimpleAnswer, SimpleAnswerData> = (data, included) => {
  const tasks = deserializeManyRelationship<Task>(data.relationships.tasks, included);

  const components =
    deserializeManyRelationship(data.relationships.simple_components, included, deserializeSimpleComponent);

  const media =
    deserializeManyRelationship(data.relationships.simple_media, included, deserializeSimpleMedium);

  const surveys =
    deserializeManyRelationship(data.relationships.simple_surveys, included, deserializeSimpleSurvey);

  return {
    ...deserializeItem(data),
    components,
    media,
    surveys,
    tasks
  };
};

interface QuestionData extends JSONAPIRecord {
  relationships: {
    validations: JSONAPIManyRelationship;
    measurements: JSONAPIManyRelationship;
    simples: JSONAPIManyRelationship;
    sliders: JSONAPIManyRelationship;
  } & JSONAPIRecord["relationships"];
}

const deserializeQuestion: Deserializer<Question, QuestionData> = (data, included) => {
  const validations = deserializeManyRelationship<Validation>(data.relationships.validations, included);

  const baseQuestion: Question = {
    ...deserializeItem(data),
    validations
  };

  switch (baseQuestion.questionType) {
    case "measurement":
      baseQuestion.answer =
        first(deserializeManyRelationship<MeasurementAnswer>(data.relationships.measurements, included));
      break;
    case "slider": {
      baseQuestion.answer =
        first(deserializeManyRelationship<SliderAnswer>(data.relationships.sliders, included));
      break;
    }
    case "multiple":
    case "single":
      baseQuestion.answers =
        deserializeManyRelationship(data.relationships.simples, included, deserializeSimpleAnswer);
      break;
    default:
      break;
  }

  return baseQuestion;
};

interface SurveyData extends JSONAPIRecord {
  relationships: {
    schedules: JSONAPIManyRelationship;
    questions: JSONAPIManyRelationship;
    survey_tags: JSONAPIManyRelationship;
  } & JSONAPIRecord["relationships"];
}

const deserializeSurvey: Deserializer<Survey, SurveyData> = (data, included) => {
  const questions = deserializeManyRelationship(data.relationships.questions, included, deserializeQuestion);
  const schedules = deserializeManyRelationship(data.relationships.schedules, included, deserializeItem<Schedule>);
  const surveyTagsAttributes = deserializeManyRelationship<SurveyTag>(data.relationships.survey_tags, included, deserializeSurveyTag);
  const tags = surveyTagsAttributes.map((t) => t.tag);

  return {
    ...deserializeItem(data),
    deleteDisabled: !!data.attributes.key,
    questions: sortBy(questions, 'position'),
    schedules,
    surveyTagsAttributes,
    tags
  };
};

export {
  deserializeSimpleComponent,
  deserializeSimpleMedium,
  deserializeSimpleSurvey,
  deserializeSurvey,
  EndSection,
  isSection,
  isSimpleQuestion,
  MeasurementResponse,
  Question,
  Schedule,
  ScheduleType,
  Section,
  SimpleAnswer,
  SimpleComponent,
  SimpleMedium,
  SimpleSurvey,
  SliderAnswer,
  Survey,
  Validation
};
